import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import {
  canAddToCart,
  getProductVariant,
  products,
  siteContent,
  toOrderProduct
} from '../data/products';
import { useContent } from './ContentContext';
import { calculateLineTotal, formatINR } from '../utils/commerce';

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export { formatINR };

const STORAGE_KEY = 'kriams_cart_v3';
const LEGACY_STORAGE_KEY = 'kriams_cart_v2';

const cartKey = (productId, variantId = '') => `${Number(productId)}:${variantId || 'base'}`;

const normalizeSavedItem = (key, value) => {
  const entry = value && typeof value === 'object'
    ? value
    : { productId: Number(key), variantId: '', quantity: value };
  const productId = Number(entry.productId);
  const product = products.find((item) => item.id === productId);
  if (!canAddToCart(product)) return null;
  const requestedVariantId = typeof entry.variantId === 'string' ? entry.variantId : '';
  if (requestedVariantId && !getProductVariant(product, requestedVariantId)) return null;
  const variantId = requestedVariantId;
  const quantity = Math.min(siteContent.ordering.maxQuantity, Math.max(0, Math.floor(Number(entry.quantity) || 0)));
  if (!quantity) return null;
  return [cartKey(productId, variantId), { productId, variantId, quantity }];
};

const loadSavedItems = () => {
  if (typeof window === 'undefined') return {};
  try {
    const saved = JSON.parse(
      window.localStorage.getItem(STORAGE_KEY)
      || window.localStorage.getItem(LEGACY_STORAGE_KEY)
      || '{}'
    );
    if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return {};
    return Object.fromEntries(
      Object.entries(saved)
        .map(([key, value]) => normalizeSavedItem(key, value))
        .filter(Boolean)
    );
  } catch {
    return {};
  }
};

export const CartProvider = ({ children }) => {
  const { revision, status } = useContent();
  const [items, setItems] = useState(() => status === 'loading' ? {} : loadSavedItems());
  const [loaded, setLoaded] = useState(status !== 'loading');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!loaded) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage may be unavailable in private browsing; the cart still works in memory.
    }
  }, [items, loaded]);

  useEffect(() => {
    if (!loaded && status !== 'loading') {
      setItems(loadSavedItems());
      setLoaded(true);
    }
  }, [loaded, status]);

  // A saved basket can outlive a Studio edit. Remove products that are no
  // longer purchasable as soon as content refreshes, rather than letting an
  // unavailable item reach WhatsApp.
  useEffect(() => {
    setItems((current) => {
      const next = Object.fromEntries(
        Object.entries(current).map(([key, line]) => normalizeSavedItem(key, line)).filter(Boolean)
      );
      return JSON.stringify(next) === JSON.stringify(current) ? current : next;
    });
  }, [revision]);

  const add = useCallback((id, requestedVariantId = '') => {
    const product = products.find((item) => item.id === Number(id));
    if (!canAddToCart(product)) return;
    const variantId = getProductVariant(product, requestedVariantId)?.id || '';
    const key = cartKey(product.id, variantId);
    setItems((prev) => ({
      ...prev,
      [key]: {
        productId: product.id,
        variantId,
        quantity: Math.min((prev[key]?.quantity || 0) + 1, siteContent.ordering.maxQuantity)
      }
    }));
  }, []);

  const decrement = useCallback((id, variantId = '') => {
    const key = cartKey(id, variantId);
    setItems((prev) => {
      const next = { ...prev };
      const quantity = (next[key]?.quantity || 0) - 1;
      if (quantity <= 0) delete next[key];
      else next[key] = { ...next[key], quantity };
      return next;
    });
  }, []);

  const remove = useCallback((id, variantId = '') => {
    const key = cartKey(id, variantId);
    setItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);
  const qtyOf = useCallback((id, variantId = '') => items[cartKey(id, variantId)]?.quantity || 0, [items]);

  const detailed = useMemo(
    () =>
      Object.entries(items)
        .map(([key, line]) => {
          const product = products.find((item) => item.id === Number(line.productId));
          if (!canAddToCart(product) || (line.variantId && !getProductVariant(product, line.variantId))) return null;
          const orderProduct = toOrderProduct(product, line.variantId);
          return {
            key,
            product: orderProduct,
            qty: line.quantity,
            lineTotal: calculateLineTotal(orderProduct.price, line.quantity)
          };
        })
        .filter(Boolean),
    // The catalog is updated in place when Studio publishes new prices.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, revision]
  );

  const count = useMemo(
    () => Object.values(items).reduce((sum, line) => sum + (Number(line.quantity) || 0), 0),
    [items]
  );
  const total = useMemo(
    () => detailed.reduce((sum, item) => sum + (item.lineTotal == null ? 0 : item.lineTotal), 0),
    [detailed]
  );

  const value = {
    items, add, decrement, remove, clear, qtyOf,
    detailed, count, total, open, setOpen
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
